/**
 * TOSS Web Application — the menu of the side navigation
 *
 * Draws a list of {@link SidebarNavNode}s and owns exactly one thing: which parents are open.
 * Both rails used to keep that themselves — the internal one in a `Set<string>` in `Sidebar.tsx`,
 * the portal in named blocks that could not open at all — and both would have needed the same
 * code once the owner settled on one model: «пункт с подпунктами или просто пункт» (26.08.2026).
 *
 * A parent opens on click and also opens ITSELF when the current screen is one of its children —
 * on mount and on every navigation after it, so following a link from elsewhere leaves the menu
 * showing where you are rather than closed over it.
 *
 * Closed children are not rendered. The internal accordion used to animate with a CSS grid, which
 * kept them in the document at zero height — invisible, and still in the tab order: a keyboard
 * user walked through ten hidden «Справочники» rows to reach the account block.
 */

import { useEffect, useState } from 'react';
import { SidebarFlyout } from './SidebarFlyout';
import { SidebarNavRow } from './SidebarNavRow';
import type { SidebarActiveMatcher, SidebarNavNode } from './types';

// ============================================================================
// Types
// ============================================================================

export interface SidebarNavProps {
  items: SidebarNavNode[];
  /** Current route — the matcher's second argument, and what auto-expansion watches. */
  currentPath: string;
  /** Whether a node is the current screen. The surfaces disagree; see `types.ts`. */
  isActive: SidebarActiveMatcher;
  collapsed?: boolean;
  /** Closes the mobile drawer after a leaf is followed. */
  onNavigate?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function parentsOfActive(
  items: SidebarNavNode[],
  currentPath: string,
  isActive: SidebarActiveMatcher
): string[] {
  return items
    .filter((item) => item.children?.some((child) => isActive(child, currentPath)))
    .map((item) => item.id);
}

// ============================================================================
// Component
// ============================================================================

export function SidebarNav({
  items,
  currentPath,
  isActive,
  collapsed = false,
  onNavigate,
}: SidebarNavProps): JSX.Element {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(parentsOfActive(items, currentPath, isActive))
  );

  // Opened, never closed, by navigation: a parent the customer folded away stays folded when they
  // move around inside a different one.
  useEffect(() => {
    const shouldOpen = parentsOfActive(items, currentPath, isActive);
    if (shouldOpen.length === 0) return;

    setExpanded((prev) => {
      if (shouldOpen.every((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      shouldOpen.forEach((id) => next.add(id));
      return next;
    });
  }, [items, currentPath, isActive]);

  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <ul className="space-y-0.5">
      {items.map((item) => {
        const children = item.children ?? [];
        const hasChildren = children.length > 0;
        const isOpen = expanded.has(item.id);
        // A parent is lit while it is CLOSED over the current screen; open, the lit child says it
        // better and two tinted rows in a column would read as two current screens.
        const childActive = children.some((child) => isActive(child, currentPath));
        const active = hasChildren ? childActive && !isOpen : isActive(item, currentPath);

        const row = (
          <SidebarNavRow
            label={item.label}
            icon={item.icon}
            to={item.path}
            isActive={active}
            collapsed={collapsed}
            badgeCount={item.badgeCount}
            expandable={hasChildren && !collapsed}
            isExpanded={isOpen}
            onToggleExpand={() => toggle(item.id)}
            onNavigate={onNavigate}
            noTooltip={collapsed && hasChildren}
          />
        );

        return (
          <li key={item.id}>
            {collapsed && hasChildren ? (
              <SidebarFlyout
                label={item.label}
                items={children}
                isItemActive={(child) => isActive(child, currentPath)}
                onNavigate={onNavigate}
              >
                {row}
              </SidebarFlyout>
            ) : (
              row
            )}

            {hasChildren && !collapsed && isOpen && (
              <ul className="mt-0.5 space-y-0.5">
                {children.map((child) => (
                  <li key={child.id}>
                    <SidebarNavRow
                      label={child.label}
                      icon={child.icon}
                      to={child.path}
                      isActive={isActive(child, currentPath)}
                      badgeCount={child.badgeCount}
                      nested
                      onNavigate={onNavigate}
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default SidebarNav;
